import React, { useState, useEffect } from 'react';
import { Package, Search, ExternalLink, ChevronLeft, ChevronRight, RefreshCw } from 'lucide-react';
import { apiRequest } from '../../../utils/api-request';
import SettingsSlideModal from '../../../components/Modal/SettingsSlideModal';
import { toastMessage } from '../../../utils/toast-message';

interface Product {
  id: string;
  title: string;
  vendor: string;
  product_type: string;
  created_at: string;
  updated_at: string;
  status: string;
  variants: Array<{
    id: string;
    title: string;
    price: string;
    inventory_quantity: number;
    re_order_days?: number;
    image_url?: string;
    sku?: string;
  }>;
  image?: {
    src: string;
  };
}

const ITEMS_PER_PAGE = 10;

type ProductStatus = 'all' | 'active' | 'draft' | 'archived';

const STATUS_TABS: { label: string; value: ProductStatus }[] = [
  { label: 'All', value: 'all' },
  { label: 'Active', value: 'active' },
  { label: 'Draft', value: 'draft' },
  { label: 'Archived', value: 'archived' },
];

const ProductsPage: React.FC = () => {
  const [products, setProducts] = useState<Product[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncProgress, setSyncProgress] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<ProductStatus>('all');
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [reOrderDays, setReOrderDays] = useState<Record<string, number>>({});
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    loadProducts();
  }, [statusFilter]);

  const loadProducts = async () => {
    setIsLoading(true);
    try {
      const response = await apiRequest(`/shopify/products?status=${statusFilter}`, { method: 'GET' });
      if (response.success && response.data) {
        setProducts(response.data);
      }
    } catch (error) {
      console.error('Failed to load products:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleStatusChange = (status: ProductStatus) => {
    setStatusFilter(status);
    setCurrentPage(1);
  };

  const filteredProducts = products.filter(product =>
    product.title.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Pagination calculations
  const totalPages = Math.ceil(filteredProducts.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const paginatedProducts = filteredProducts.slice(startIndex, endIndex);

  // Reset to page 1 when search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  const handleProductClick = (product: Product) => {
    setSelectedProduct(product);
    setIsModalOpen(true);
    // Initialize re-order days from product data
    const initialDays: Record<string, number> = {};
    product.variants.forEach(variant => {
      if (variant.re_order_days) {
        initialDays[variant.id] = variant.re_order_days;
      }
    });
    setReOrderDays(initialDays);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedProduct(null);
    setReOrderDays({});
  };

  const handleReOrderDaysChange = (variantId: string, days: number) => {
    setReOrderDays(prev => ({
      ...prev,
      [variantId]: days
    }));
  };

  const handleSyncProducts = async () => {
    setIsSyncing(true);
    setSyncProgress('Fetching products from Shopify...');
    try {
      const response = await apiRequest('/shopify/products/sync', {
        method: 'POST'
      });

      if (response.success) {
        setSyncProgress(`Synced ${response.data.synced_products} products in ${response.data.elapsed_seconds}s`);
        toastMessage(`✓ Synced ${response.data.synced_products} products successfully!`, 'success');

        // Reload products list
        await new Promise(resolve => setTimeout(resolve, 500));
        loadProducts();
      } else {
        toastMessage(response.message || 'Sync failed', 'error');
      }
    } catch (error) {
      console.error('Failed to sync products:', error);
      toastMessage('Failed to sync products', 'error');
    } finally {
      setIsSyncing(false);
      setSyncProgress('');
    }
  };

  const handleSaveReOrderDays = async () => {
    if (!selectedProduct) return;

    setIsSaving(true);
    try {
      // Save re-order days for each variant
      const updates = Object.entries(reOrderDays).map(([variantId, days]) => ({
        variant_id: variantId,
        re_order_days: days
      }));

      const response = await apiRequest('/shopify/variants/re-order-days', {
        method: 'POST',
        body: { updates }
      });

      if (response.success) {
        // Update local state
        setProducts(prev => prev.map(p => {
          if (p.id === selectedProduct.id) {
            return {
              ...p,
              variants: p.variants.map(v => ({
                ...v,
                re_order_days: reOrderDays[v.id] || v.re_order_days
              }))
            };
          }
          return p;
        }));
        toastMessage('Re-order days saved successfully!', 'success');
      }
    } catch (error) {
      console.error('Failed to save re-order days:', error);
      toastMessage('Failed to save re-order days', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="w-full">
      {/* Custom Header with Search - Fixed */}
      <div className="sticky top-0 z-10 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4">
        <div className="flex items-center justify-between gap-6">
          {/* Left: Title and Description */}
          <div className="flex items-center space-x-3 flex-1">
            <div className="flex-shrink-0">
              <div className="p-2 bg-brandColor rounded-lg text-white">
                <Package className="w-6 h-6" />
              </div>
            </div>
            <div className="flex-1 min-w-0">
              <h1 className="text-lg font-semibold text-gray-900 dark:text-white">
                Products
              </h1>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                View and manage your Shopify products
              </p>
            </div>
          </div>

          {/* Right: Search Bar */}
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search products by name..."
              className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-brandColor focus:border-transparent dark:bg-gray-800 dark:text-white"
            />
          </div>
        </div>

        {/* Status Filter Tabs + Sync Button */}
        <div className="flex items-center justify-between mt-4">
          <div className="flex items-center space-x-1">
            {STATUS_TABS.map((tab) => (
              <button
                key={tab.value}
                onClick={() => handleStatusChange(tab.value)}
                className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                  statusFilter === tab.value
                    ? 'bg-brandColor text-white'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Sync Button with Progress */}
          <div className="flex items-center space-x-3">
            {isSyncing && syncProgress && (
              <div className="flex items-center space-x-2">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-brandColor"></div>
                <span className="text-sm text-gray-600 dark:text-gray-400">{syncProgress}</span>
              </div>
            )}
            <button
              onClick={handleSyncProducts}
              disabled={isSyncing}
              className="flex items-center space-x-2 px-4 py-2 bg-brandColor text-white rounded-lg hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
            >
              <RefreshCw className={`h-4 w-4 ${isSyncing ? 'animate-spin' : ''}`} />
              <span className="text-sm font-medium">
                {isSyncing ? 'Syncing...' : 'Sync Products'}
              </span>
            </button>
          </div>
        </div>
      </div>
      <div className="p-6 bg-gray-50 dark:bg-gray-900 min-h-screen">
        <div className="w-full">

        {/* Content */}
        {isLoading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brandColor"></div>
          </div>
        ) : filteredProducts.length === 0 ? (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-12 text-center">
            <Package className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">No products found</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
              {searchQuery ? 'Try adjusting your search query' : 'Connect your Shopify store to view products'}
            </p>
            {!searchQuery && (
              <a
                href="/shopify-admin/profile"
                className="inline-flex items-center space-x-2 text-brandColor hover:underline"
              >
                <span>Configure Shopify Profile</span>
                <ExternalLink className="h-4 w-4" />
              </a>
            )}
          </div>
        ) : (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 dark:bg-gray-750 border-b border-gray-200 dark:border-gray-700">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Product
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Product ID
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Type
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Variants
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Price (INR)
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Vendor
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {paginatedProducts.map((product) => (
                    <tr key={product.id} className="hover:bg-gray-50 dark:hover:bg-gray-750">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          {product.image?.src && (
                            <img src={product.image.src} alt={product.title} className="h-10 w-10 rounded object-cover mr-3" />
                          )}
                          <div>
                            <button
                              onClick={() => handleProductClick(product)}
                              className="text-sm font-medium text-brandColor hover:opacity-80 cursor-pointer text-left"
                            >
                              {product.title}
                            </button>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                        #{product.id}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                        {product.product_type}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 text-xs rounded-full ${
                          product.status === 'active'
                            ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300'
                            : product.status === 'draft'
                            ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300'
                            : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                        }`}>
                          {product.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                        {product.variants.length}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                        ₹{product.variants[0]?.price || '0.00'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                        {product.vendor}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination Controls */}
            {totalPages > 1 && (
              <div className="px-6 py-4 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between">
                <div className="text-sm text-gray-700 dark:text-gray-300">
                  Showing <span className="font-medium">{startIndex + 1}</span> to{' '}
                  <span className="font-medium">{Math.min(endIndex, filteredProducts.length)}</span> of{' '}
                  <span className="font-medium">{filteredProducts.length}</span> products
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                    disabled={currentPage === 1}
                    className="px-3 py-1 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-1"
                  >
                    <ChevronLeft className="h-4 w-4" />
                    <span>Previous</span>
                  </button>

                  <div className="flex items-center space-x-1">
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => {
                      // Show first page, last page, current page, and pages around current
                      if (
                        page === 1 ||
                        page === totalPages ||
                        (page >= currentPage - 1 && page <= currentPage + 1)
                      ) {
                        return (
                          <button
                            key={page}
                            onClick={() => setCurrentPage(page)}
                            className={`px-3 py-1 rounded-md border ${
                              page === currentPage
                                ? 'bg-brandColor text-white border-brandColor'
                                : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600'
                            }`}
                          >
                            {page}
                          </button>
                        );
                      } else if (page === currentPage - 2 || page === currentPage + 2) {
                        return <span key={page} className="text-gray-500">...</span>;
                      }
                      return null;
                    })}
                  </div>

                  <button
                    onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                    disabled={currentPage === totalPages}
                    className="px-3 py-1 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-1"
                  >
                    <span>Next</span>
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
        </div>
      </div>

      {/* Product Details Slider */}
      <SettingsSlideModal
        isOpen={isModalOpen}
        onClose={closeModal}
        title={selectedProduct?.title || ''}
        subtitle={`Product ID: #${selectedProduct?.id}`}
        icon={selectedProduct?.image?.src ? (
          <img src={selectedProduct.image.src} alt={selectedProduct.title} className="h-8 w-8 rounded object-cover" />
        ) : (
          <Package className="w-6 h-6" />
        )}
        width="800px"
      >
        {selectedProduct && (
          <>
            <div className="flex-1 overflow-y-auto">
              <div className="px-6 py-4 space-y-6">
                {/* Product Info Grid */}
                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div>
                    <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Vendor</label>
                    <p className="text-sm text-gray-900 dark:text-white mt-1">{selectedProduct.vendor || 'N/A'}</p>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Type</label>
                    <p className="text-sm text-gray-900 dark:text-white mt-1">{selectedProduct.product_type || 'N/A'}</p>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Status</label>
                    <span className={`inline-block px-2 py-1 text-xs rounded-full mt-1 ${
                      selectedProduct.status === 'active'
                        ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300'
                        : selectedProduct.status === 'draft'
                        ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300'
                        : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                    }`}>
                      {selectedProduct.status}
                    </span>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Created At</label>
                    <p className="text-sm text-gray-900 dark:text-white mt-1">
                      {new Date(selectedProduct.created_at).toLocaleDateString()}
                    </p>
                  </div>
                </div>

                {/* Variants Table */}
                <div>
                  <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">
                    Variants ({selectedProduct.variants.length})
                  </h4>
                  {selectedProduct.variants.length > 0 ? (
                    <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                      <table className="w-full">
                        <thead className="bg-gray-50 dark:bg-gray-750">
                          <tr>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                              Image
                            </th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                              Variant ID
                            </th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                              Title
                            </th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                              SKU
                            </th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                              Price (INR)
                            </th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                              Re-order Days
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200 dark:divide-gray-700 bg-white dark:bg-gray-800">
                          {selectedProduct.variants.map((variant) => (
                            <tr key={variant.id}>
                              <td className="px-4 py-3">
                                {variant.image_url || selectedProduct.image?.src ? (
                                  <img
                                    src={variant.image_url || selectedProduct.image?.src}
                                    alt={variant.title}
                                    className="h-10 w-10 rounded object-cover"
                                  />
                                ) : (
                                  <div className="h-10 w-10 rounded bg-gray-200 dark:bg-gray-700 flex items-center justify-center">
                                    <Package className="h-5 w-5 text-gray-400" />
                                  </div>
                                )}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
                                #{variant.id}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                                {variant.title}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
                                {variant.sku || 'N/A'}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                                ₹{variant.price}
                              </td>
                              <td className="px-4 py-3">
                                <input
                                  type="number"
                                  min="0"
                                  value={reOrderDays[variant.id] || ''}
                                  onChange={(e) => handleReOrderDaysChange(variant.id, parseInt(e.target.value) || 0)}
                                  placeholder="Enter days"
                                  className="w-24 px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded focus:outline-none focus:border-brandColor dark:bg-gray-700 dark:text-white"
                                />
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500 dark:text-gray-400">No variants available</p>
                  )}
                </div>
              </div>
            </div>

            {/* Footer with Save Button */}
            <div className="px-6 py-4 bg-gray-50 dark:bg-gray-750 border-t border-gray-200 dark:border-gray-700 flex justify-end space-x-3">
              <button
                onClick={closeModal}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveReOrderDays}
                disabled={isSaving}
                className="px-4 py-2 bg-brandColor text-white rounded-lg hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
              >
                {isSaving ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span>Saving...</span>
                  </>
                ) : (
                  <span>Save Re-order Days</span>
                )}
              </button>
            </div>
          </>
        )}
      </SettingsSlideModal>
    </div>
  );
};

export default ProductsPage;
